import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Loader2, BadgeCheck, ShieldCheck, ArrowRight, Clock3 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const looksLikeEmail = (value) => value.includes("@") || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

function ImageUpload({ label, value, onChange, testId }) {
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const acceptedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
    if (!acceptedTypes.has(file.type)) {
      toast.error("Use a JPEG, PNG, or WebP image.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <label className="block cursor-pointer" data-testid={`${testId}-label`}>
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
      <div className={`relative rounded-2xl border-2 border-dashed transition-colors p-6 h-56 flex items-center justify-center overflow-hidden ${
          value ? "border-indigo-400/40" : "border-white/10 hover:border-white/30"
        }`}>
        {value ? (
          <img src={value} alt={label} className="absolute inset-0 h-full w-full object-cover"/>
        ) : (
          <div className="text-center text-zinc-500">
            <Upload size={22} className="mx-auto"/>
            <div className="text-sm mt-2">Tap to upload</div>
            <div className="text-xs">JPG/PNG, up to 5MB</div>
          </div>
        )}
        <input data-testid={testId} type="file" accept="image/jpeg,image/png,image/webp" className="absolute inset-0 opacity-0 cursor-pointer" onChange={onFile}/>
      </div>
    </label>
  );
}

export default function Verify() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    college_id_image: "", selfie_image: "",
    college_name: user?.college || "", course: user?.course || "", year: user?.year || "",
    student_id_number: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const isCollegeEmail = user?.verification_method === "college_email";
  const studentIdLooksLikeEmail = looksLikeEmail(f.student_id_number);

  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const startRenewal = async () => {
    setRenewing(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/start-reverification");
      setUser(data.user);
      toast.success("We sent a fresh verification code to your email.");
      nav("/verify-email", {
        replace: true,
        state: {
          email: data.user.email,
          dev_otp: data.dev_otp,
          email_sent: data.email_sent,
        },
      });
    } catch (e) {
      const message = formatApiError(e.response?.data?.detail);
      setErr(message);
      toast.error(message);
    } finally {
      setRenewing(false);
    }
  };

  if (
    user?.verification_status === "expired"
    && !user?.reverification_email_verified
  ) {
    return (
      <div className="min-h-screen bg-[#050505] grain">
        <Navbar/>
        <div className="max-w-2xl mx-auto px-6 pt-32 text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-amber-500/15 border border-amber-400/30 items-center justify-center">
            <ShieldCheck className="text-amber-300" size={32}/>
          </div>
          <h1 className="font-display text-4xl font-extrabold mt-4">Renew your student verification</h1>
          <p className="text-zinc-300 mt-4">Your one-year student verification has expired.</p>
          <p className="text-zinc-400 mt-2">First, verify your email again. You can correct the address on the OTP screen if needed.</p>
          {err && <div className="mt-4 text-sm text-red-400">{err}</div>}
          <button
            data-testid="verify-start-renewal"
            onClick={startRenewal}
            disabled={renewing}
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold px-6 py-3 disabled:opacity-60"
          >
            {renewing ? <Loader2 size={16} className="animate-spin"/> : <>Verify email and renew <ArrowRight size={14}/></>}
          </button>
        </div>
      </div>
    );
  }

  if (submitted || user?.verification_status === "pending") {
    return (
      <div className="min-h-screen bg-[#050505] grain">
        <Navbar/>
        <div className="max-w-2xl mx-auto px-6 pt-32 text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-indigo-500/15 border border-indigo-400/30 items-center justify-center">
            <Clock3 className="text-indigo-300" size={32}/>
          </div>
          <h1 className="font-display text-4xl font-extrabold mt-4">Verification Submitted</h1>
          <p className="text-zinc-300 mt-4">Your documents have been received successfully.</p>
          <p className="text-zinc-400 mt-2">Our team is reviewing your verification. Verification is usually completed within 24 hours.</p>
          <p className="text-zinc-500 mt-2">You'll receive an email once the review is complete.</p>
          <Link to="/dashboard" className="mt-7 inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold px-6 py-3">
            Go to dashboard <ArrowRight size={14}/>
          </Link>
        </div>
      </div>
    );
  }

  if (user?.verification_status === "approved") {
    return (
      <div className="min-h-screen bg-[#050505] grain">
        <Navbar/>
        <div className="max-w-2xl mx-auto px-6 pt-32 text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 items-center justify-center">
            <BadgeCheck className="text-emerald-400" size={32}/>
          </div>
          <h1 className="font-display text-4xl font-extrabold mt-4">You're verified!</h1>
          <p className="text-zinc-400 mt-2">Your student pass is ready. Head to your dashboard.</p>
          <Link to="/dashboard" data-testid="verify-go-dashboard" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold px-6 py-3">
            Go to dashboard <ArrowRight size={14}/>
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (studentIdLooksLikeEmail) {
      setErr("Enter your Student ID / Roll Number, not your email address.");
      return;
    }
    if (!isCollegeEmail && (!f.college_id_image || !f.selfie_image)) {
      setErr("Upload both your college ID card and a selfie holding it to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/verification/submit", f);
      setUser(data.user);
      setSubmitted(data.user.verification_status !== "approved");
      toast.success(isCollegeEmail ? "Verification complete." : "Verification submitted for review.");
    } catch (e) {
      const message = formatApiError(e.response?.data?.detail);
      setErr(message);
      toast.error(message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain">
      <Navbar/>
      <div className="aurora bg-indigo-600/30" style={{ width: 500, height: 500, top: 0, right: -100 }} />
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs text-indigo-200 mb-4">
            <ShieldCheck size={12}/> Student verification
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">Prove you're a student</h1>
          <p className="text-zinc-400 mt-3 max-w-xl">
            {isCollegeEmail
              ? "Your approved college email lets us verify your student status from your academic details."
              : "Upload your college ID and a selfie holding it so our team can review your student status."}
          </p>
        </motion.div>

        <form onSubmit={onSubmit} className="mt-10 glass-heavy rounded-3xl p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={`md:col-span-2 rounded-2xl p-4 text-sm ${isCollegeEmail ? "bg-emerald-500/10 border border-emerald-400/30 text-emerald-100" : "bg-amber-500/10 border border-amber-400/30 text-amber-100"}`}>
            {isCollegeEmail ? (
              <>
                <span className="font-semibold">College email verification:</span>{" "}
                no document uploads are needed. Complete and submit the academic
                details below, including your Student ID / Roll Number, to get
                verified.
              </>
            ) : <><span className="font-semibold">Documents required:</span> both uploads are required for manual review.</>}
          </div>
          {!isCollegeEmail && <>
            <ImageUpload label="College ID card image *" value={f.college_id_image} onChange={(v) => update("college_id_image", v)} testId="verify-collegeid-input"/>
            <ImageUpload label="Selfie holding college ID card *" value={f.selfie_image} onChange={(v) => update("selfie_image", v)} testId="verify-selfie-input"/>
          </>}

          {[
            { k: "college_name", label: "College name", required: true },
            { k: "student_id_number", label: "Student ID / Roll Number", required: true },
            { k: "course", label: "Course (e.g. B.Tech CSE)", required: true },
            {
              k: "year",
              label: "Year of study",
              placeholder: "e.g. 1st year",
              helper: "Enter as 1st year, 2nd year, 3rd year, etc.",
              required: true,
            },
          ].map((field) => (
            <div key={field.k}>
              <label className="text-xs uppercase tracking-widest text-zinc-500">{field.label}</label>
              <input
                data-testid={`verify-${field.k.replace(/_/g,'-')}-input`}
                required={field.required}
                value={f[field.k]}
                placeholder={field.placeholder}
                onChange={(e) => update(field.k, e.target.value)}
                autoComplete={field.k === "student_id_number" ? "off" : undefined}
                aria-describedby={field.k === "student_id_number" ? "student-id-help" : undefined}
                className={`mt-2 w-full rounded-xl bg-white/5 border px-4 py-3 text-white focus:ring-2 focus:outline-none ${field.k === "student_id_number" && studentIdLooksLikeEmail ? "border-red-400 focus:border-red-400 focus:ring-red-500/40" : "border-white/10 focus:border-indigo-400 focus:ring-indigo-500/40"}`}
              />
              {field.k === "student_id_number" && (
                <p id="student-id-help" className={`mt-2 text-xs leading-relaxed ${studentIdLooksLikeEmail ? "text-red-300" : "text-zinc-500"}`}>
                  {studentIdLooksLikeEmail
                    ? "Please enter your Student ID / Roll Number — email addresses are not accepted here."
                    : "Use the Student ID or Roll Number shown on your college records. Do not enter your email address."}
                </p>
              )}
              {field.helper && (
                <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                  {field.helper}
                </p>
              )}
            </div>
          ))}

          {err && <div data-testid="verify-error" className="md:col-span-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{err}</div>}
          <button data-testid="verify-submit-btn" disabled={loading} className="md:col-span-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-semibold py-3 hover:scale-[1.02] transition-transform disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin"/> : <>Submit for verification <ArrowRight size={14}/></>}
          </button>
        </form>
      </div>
    </div>
  );
}
